import { Injectable, inject } from '@angular/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { SocketService } from './socket.service';
import { TokenService } from './token.service';

@Injectable({ providedIn: 'root' })
export class PaymentNotificationService {
  private readonly socket  = inject(SocketService);
  private readonly tokens  = inject(TokenService);
  private initialized = false;

  /** Call once after login (or on app resume if token is present). */
  async init(): Promise<void> {
    if (this.initialized) return;

    const token = this.tokens.getToken();
    const driverId = this.getDriverIdFromToken();
    if (!driverId || !token) return;

    // Authenticate socket with JWT so update-location is accepted by the gateway.
    // This covers app restarts where the user is already logged in.
    this.socket.authenticate(token);

    // Request local notification permission
    const { display } = await LocalNotifications.requestPermissions();
    if (display !== 'granted') {
      console.warn('[PaymentNotification] Local notification permission denied.');
    }

    this.socket.emit('join-driver', driverId);
    console.log(`[PaymentNotification] Joined driver-${driverId} room.`);

    // Listen for payment_received events
    this.socket.on('payment_received', async (data: { amount: string; message: string }) => {
      console.log('[PaymentNotification] payment_received:', data);
      await this.showNotification(data.message, data.amount);
    });

    this.initialized = true;
  }

  /** Reset so init() can be called again after re-login. */
  reset(): void {
    this.initialized = false;
  }

  private getDriverIdFromToken(): number | null {
    try {
      const token = this.tokens.getToken();
      if (!token) return null;
      const b64     = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const binary  = atob(b64);
      const bytes   = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const payload = JSON.parse(new TextDecoder().decode(bytes));
      return payload.driverId ?? null;
    } catch {
      return null;
    }
  }

  private async showNotification(title: string, amount: string): Promise<void> {
    try {
      await LocalNotifications.schedule({
        notifications: [{
          id:    Math.floor(Math.random() * 100000),
          title: `Pago de pase realizado Bs. ${amount}`,
          body:  'El pasajero pagó su pase con saldo syncc.',
          sound: 'default',
          smallIcon: 'ic_stat_icon_config_sample',
          extra: { amount },
        }],
      });
    } catch (e) {
      console.error('[PaymentNotification] Failed to show notification:', e);
    }
  }
}
